import { IAPIRoutes } from "../../../../config";
import type {
  AvailableWorkflowsResponse,
  AvailableWorkflowWire,
  ClinicWorkflowArtifactUrl,
  ClinicWorkflowEnablementResponse,
  ClinicWorkflowRunsResponse,
  ClinicWorkflowRunTriggerResponse,
  ClinicWorkflowStatusResponse,
  EligibleClinicDTO,
  INeoWorkflowRepository,
  InboundEmailAddressDTO,
  InboundEmailBindingCreatePayload,
  InboundEmailBindingDTO,
  InboundEmailBindingPatchPayload,
  MondayBriefingDeliveriesResponse,
  MondayBriefingEmail,
  MondayBriefingRunStatus,
  WorkflowCreatePayload,
  WorkflowEdgeInput,
  WorkflowGraph,
  WorkflowListResponse,
  WorkflowMeta,
  WorkflowNodeClinicScopeCheckPayload,
  WorkflowNodeClinicScopeCheckResult,
  WorkflowNodeInput,
  WorkflowNodeSchemaGenResult,
  WorkflowRunConversationsResponse,
  WorkflowRunListResponse,
  WorkflowRunProgress,
  WorkflowRunResult,
  WorkflowUpdatePayload,
  SimulatedEmailPayload,
} from "../../../core/ports/driven/neoWorkflowRepository";

const fillRoute = (
  template: string,
  params: Record<string, string | number>
): string => {
  let out = template;
  for (const [key, value] of Object.entries(params)) {
    out = out.replace(`{${key}}`, String(value));
  }
  return out;
};

/** Thrown when a graph save is rejected for optimistic-concurrency reasons
 *  (HTTP 409): the client's `base_revision` is stale because another writer
 *  changed the graph. The APIProvider resolves error responses with the body
 *  rather than throwing, so we detect the 409 marker here and raise this. */
export class GraphRevisionConflictError extends Error {
  /** Duck-typed marker so the service can detect this without importing the
   *  class across the hexagon boundary. */
  readonly graphRevisionConflict = true as const;
  readonly currentRevision: number | null;

  constructor(currentRevision: number | null) {
    super("The workflow graph changed since it was loaded.");
    this.name = "GraphRevisionConflictError";
    this.currentRevision = currentRevision;
  }
}

/** Recognize the backend's 409 conflict body shape (FastAPI `detail`). */
const isRevisionConflictBody = (
  body: unknown
): body is { detail: { error: string; current_revision?: number | null } } => {
  if (!body || typeof body !== "object") return false;
  const detail = (body as { detail?: unknown }).detail;
  return (
    !!detail &&
    typeof detail === "object" &&
    (detail as { error?: unknown }).error === "graph_revision_conflict"
  );
};

export type INeoWorkflowRepositoryFactory = ReturnType<
  typeof NeoWorkflowRepositoryFactory
>;

export const NeoWorkflowRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}): INeoWorkflowRepository => {
  const { GET, POST, PUT, PATCH, DELETE } = APIProvider;
  const { getAuthenticationToken } = AuthService;

  const authHeader = async () => ({
    Authorization: `Bearer ${await getAuthenticationToken()}`,
  });

  const listWorkflowsAPI = async (): Promise<WorkflowListResponse> => {
    return GET(APIRoutes.apiNeoWorkflows, { limit: 100 }, await authHeader());
  };

  const createWorkflowAPI = async (
    payload: WorkflowCreatePayload
  ): Promise<WorkflowMeta> => {
    return POST(APIRoutes.apiNeoWorkflows, payload, await authHeader());
  };

  const listAvailableWorkflowsAPI = async (
    clinicId: number | string
  ): Promise<AvailableWorkflowsResponse> => {
    // Onboarding reads the ``/enabled`` view (the clinic catalog annotated with
    // the caller's current ``subscribed`` state + runtime fields) so the step
    // can pre-check the toggles the user is already opted into. The bare
    // ``/available`` catalog does not report per-user opt-ins.
    const resp = await GET(
      fillRoute(APIRoutes.apiNeoWorkflowsEnabled, { clinic_id: clinicId }),
      {},
      await authHeader()
    );
    // The provider returns (not throws) a non-2xx body; reject a FastAPI error
    // (`{ detail }`) so the caller sees a failure instead of an empty catalog.
    if (resp && typeof resp === "object" && "detail" in resp) {
      const detail = (resp as { detail?: unknown }).detail;
      throw new Error(
        typeof detail === "string" ? detail : JSON.stringify(detail)
      );
    }
    // Expected: { clinic_id, workflows: [{ workflow_id_str, name, description,
    // category, subscribed, ... }] }. Tolerate a bare array or a legacy
    // { items: [...] }. Onboarding only consumes id/name/description/category
    // plus subscribed; the extra runtime fields (status/last_run_at/…) are
    // ignored here.
    const rawList = Array.isArray(resp)
      ? resp
      : (resp as { workflows?: unknown })?.workflows ??
        (resp as { items?: unknown })?.items ??
        [];
    const workflows: AvailableWorkflowWire[] = (
      Array.isArray(rawList) ? rawList : []
    ).map((entry) => {
      if (entry && typeof entry === "object") {
        const o = entry as Record<string, unknown>;
        return {
          workflow_id_str: String(o.workflow_id_str ?? o.id ?? ""),
          name: (o.name as string | null | undefined) ?? null,
          description: (o.description as string | null | undefined) ?? null,
          category: (o.category as string | null | undefined) ?? null,
          extra_info:
            (o.extra_info as Record<string, unknown> | null | undefined) ??
            null,
          subscribed: (o.subscribed as boolean | null | undefined) ?? null,
          status: (o.status as AvailableWorkflowWire["status"]) ?? null,
          last_run_at: (o.last_run_at as string | null | undefined) ?? null,
          next_run_at: (o.next_run_at as string | null | undefined) ?? null,
          schedule: (o.schedule as AvailableWorkflowWire["schedule"]) ?? null,
        };
      }
      return {
        workflow_id_str: String(entry),
        name: null,
        description: null,
        category: null,
        extra_info: null,
        subscribed: null,
        status: null,
        last_run_at: null,
        next_run_at: null,
        schedule: null,
      };
    });
    return {
      clinic_id: (resp as { clinic_id?: number })?.clinic_id,
      workflows,
    };
  };

  const setWorkflowUserEnablementAPI = async (
    clinicId: number | string,
    workflowId: number | string,
    userId: number,
    enabled: boolean
  ): Promise<ClinicWorkflowEnablementResponse> => {
    return PUT(
      fillRoute(APIRoutes.apiNeoWorkflowAssignUser, {
        clinic_id: clinicId,
        workflow_id: workflowId,
        user_id: userId,
      }),
      { enabled },
      await authHeader()
    );
  };

  const runClinicWorkflowAPI = async (
    clinicId: number | string,
    workflowId: string
  ): Promise<ClinicWorkflowRunTriggerResponse> => {
    // Empty body: `only_recipients` is superadmin/production-only, so the run
    // always goes to the caller via `only_me`.
    const resp = await POST(
      `${fillRoute(APIRoutes.apiNeoClinicWorkflowRun, {
        clinic_id: clinicId,
        workflow_id: workflowId,
      })}?only_me=true`,
      {},
      await authHeader()
    );
    // The provider returns (not throws) a non-2xx body, so a FastAPI error
    // (`{ detail }`) has to be turned into a rejection here.
    if (resp && typeof resp === "object" && "detail" in resp) {
      const detail = (resp as { detail?: unknown }).detail;
      throw new Error(
        typeof detail === "string" ? detail : JSON.stringify(detail)
      );
    }
    return resp as ClinicWorkflowRunTriggerResponse;
  };

  const getWorkflowGraphAPI = async (
    workflowId: number
  ): Promise<WorkflowGraph> => {
    return GET(
      fillRoute(APIRoutes.apiNeoWorkflowGraph, { workflow_id: workflowId }),
      {},
      await authHeader()
    );
  };

  const replaceWorkflowGraphAPI = async (
    workflowId: number,
    nodes: WorkflowNodeInput[],
    edges: WorkflowEdgeInput[],
    baseRevision?: number | null
  ): Promise<WorkflowGraph> => {
    const body: {
      nodes: WorkflowNodeInput[];
      edges: WorkflowEdgeInput[];
      base_revision?: number;
    } = { nodes, edges };
    if (baseRevision != null) {
      body.base_revision = baseRevision;
    }
    const resp = await PUT(
      fillRoute(APIRoutes.apiNeoWorkflowGraph, { workflow_id: workflowId }),
      body,
      await authHeader()
    );
    // The APIProvider resolves non-2xx responses with the error body instead
    // of throwing, so translate the 409 conflict marker into a typed throw.
    if (isRevisionConflictBody(resp)) {
      throw new GraphRevisionConflictError(
        resp.detail.current_revision ?? null
      );
    }
    return resp;
  };

  const restoreLastRunGraphAPI = async (
    workflowId: number
  ): Promise<WorkflowGraph> => {
    return POST(
      fillRoute(APIRoutes.apiNeoWorkflowRestoreLastRun, {
        workflow_id: workflowId,
      }),
      {},
      await authHeader()
    );
  };

  const keepCurrentGraphVersionAPI = async (
    workflowId: number
  ): Promise<WorkflowGraph> => {
    return POST(
      fillRoute(APIRoutes.apiNeoWorkflowKeepCurrentVersion, {
        workflow_id: workflowId,
      }),
      {},
      await authHeader()
    );
  };

  const generateNodeOutputSchemaAPI = async (
    workflowId: number,
    nodeId: number
  ): Promise<WorkflowNodeSchemaGenResult> => {
    return POST(
      fillRoute(APIRoutes.apiNeoWorkflowNodeOutputSchema, {
        workflow_id: workflowId,
        node_id: nodeId,
      }),
      {},
      await authHeader()
    );
  };

  const checkNodeClinicScopeAPI = async (
    workflowId: number,
    nodeId: number,
    payload: WorkflowNodeClinicScopeCheckPayload = {}
  ): Promise<WorkflowNodeClinicScopeCheckResult> => {
    return POST(
      fillRoute(APIRoutes.apiNeoWorkflowNodeClinicScope, {
        workflow_id: workflowId,
        node_id: nodeId,
      }),
      payload,
      await authHeader()
    );
  };

  const updateWorkflowAPI = async (
    workflowId: number,
    payload: WorkflowUpdatePayload
  ): Promise<WorkflowMeta> => {
    return PATCH(
      fillRoute(APIRoutes.apiNeoWorkflow, { workflow_id: workflowId }),
      payload,
      await authHeader()
    );
  };

  const deleteWorkflowAPI = async (workflowId: number): Promise<void> => {
    await DELETE(
      fillRoute(APIRoutes.apiNeoWorkflow, { workflow_id: workflowId }),
      {},
      await authHeader()
    );
  };

  const runWorkflowAPI = async (
    workflowId: number,
    options?: {
      input_file_ids?: number[];
      simulated_email?: SimulatedEmailPayload;
      clinic_id?: number;
    }
  ): Promise<WorkflowRunResult> => {
    const body: {
      input_file_ids?: number[];
      simulated_email?: SimulatedEmailPayload;
      clinic_id?: number;
    } = {};
    if (options?.input_file_ids && options.input_file_ids.length > 0) {
      body.input_file_ids = options.input_file_ids;
    }
    if (options?.simulated_email) {
      body.simulated_email = options.simulated_email;
    }
    if (options?.clinic_id != null) {
      body.clinic_id = options.clinic_id;
    }
    return POST(
      fillRoute(APIRoutes.apiNeoWorkflowRun, { workflow_id: workflowId }),
      body,
      await authHeader()
    );
  };

  const getWorkflowRunProgressAPI = async (
    runId: string
  ): Promise<WorkflowRunProgress> => {
    return GET(
      fillRoute(APIRoutes.apiNeoWorkflowRunProgress, { run_id: runId }),
      {},
      await authHeader()
    );
  };

  const listWorkflowRunsAPI = async (
    workflowId: number,
    limit = 20
  ): Promise<WorkflowRunListResponse> => {
    return GET(
      fillRoute(APIRoutes.apiNeoWorkflowRuns, { workflow_id: workflowId }),
      { limit },
      await authHeader()
    );
  };

  const getClinicWorkflowStatusAPI = async (
    clinicId: number,
    workflowId: string
  ): Promise<ClinicWorkflowStatusResponse> =>
    GET(
      fillRoute(APIRoutes.apiGetClinicWorkflowStatus, {
        clinic_id: clinicId,
        workflow_id_str: workflowId,
      }),
      {},
      await authHeader()
    );

  const listClinicWorkflowRunsAPI = async (
    clinicId: number,
    workflowId?: string
  ): Promise<ClinicWorkflowRunsResponse> =>
    GET(
      fillRoute(APIRoutes.apiListClinicWorkflowRuns, {
        clinic_id: clinicId,
      }),
      {
        ...(workflowId ? { workflow_id_str: workflowId } : {}),
        limit: 200,
        offset: 0,
      },
      await authHeader()
    );

  const getClinicWorkflowArtifactUrlAPI = async (
    clinicId: number,
    runId: string,
    fileId: number
  ): Promise<ClinicWorkflowArtifactUrl> =>
    GET(
      fillRoute(APIRoutes.apiGetClinicWorkflowArtifactUrl, {
        clinic_id: clinicId,
        run_id: runId,
        file_id: fileId,
      }),
      {},
      await authHeader()
    );

  const listMondayBriefingDeliveriesAPI = async (
    clinicId: number
  ): Promise<MondayBriefingDeliveriesResponse> =>
    GET(
      APIRoutes.apiMondayBriefingDeliveries,
      { clinic_id: clinicId },
      await authHeader()
    );

  const getMondayBriefingDeliveryEmailAPI = async (
    rowId: number
  ): Promise<MondayBriefingEmail> =>
    GET(
      fillRoute(APIRoutes.apiMondayBriefingDeliveryEmail, { row_id: rowId }),
      {},
      await authHeader()
    );

  const getMondayBriefingRunAPI = async (
    runId: string
  ): Promise<MondayBriefingRunStatus> =>
    GET(
      fillRoute(APIRoutes.apiMondayBriefingRun, { run_id: runId }),
      {},
      await authHeader()
    );

  const listWorkflowRunConversationsAPI = async (
    runId: string
  ): Promise<WorkflowRunConversationsResponse> => {
    return GET(
      fillRoute(APIRoutes.apiNeoWorkflowRunConversations, { run_id: runId }),
      {},
      await authHeader()
    );
  };

  const listWorkflowEligibleClinicsAPI = async (
    workflowId: number
  ): Promise<{ items: EligibleClinicDTO[] }> => {
    return GET(
      fillRoute(APIRoutes.apiNeoWorkflowEligibleClinics, {
        workflow_id: workflowId,
      }),
      {},
      await authHeader()
    );
  };

  const listWorkflowEmailAddressesAPI = async (
    workflowId: number
  ): Promise<{ items: InboundEmailAddressDTO[] }> => {
    return GET(
      fillRoute(APIRoutes.apiNeoWorkflowEmailAddresses, {
        workflow_id: workflowId,
      }),
      {},
      await authHeader()
    );
  };

  const listWorkflowEmailBindingsAPI = async (
    workflowId: number
  ): Promise<{ items: InboundEmailBindingDTO[] }> => {
    return GET(
      fillRoute(APIRoutes.apiNeoWorkflowEmailBindings, {
        workflow_id: workflowId,
      }),
      {},
      await authHeader()
    );
  };

  const createWorkflowEmailBindingAPI = async (
    workflowId: number,
    payload: InboundEmailBindingCreatePayload
  ): Promise<InboundEmailBindingDTO> => {
    return POST(
      fillRoute(APIRoutes.apiNeoWorkflowEmailBindings, {
        workflow_id: workflowId,
      }),
      payload,
      await authHeader()
    );
  };

  const patchWorkflowEmailBindingAPI = async (
    workflowId: number,
    bindingId: number,
    payload: InboundEmailBindingPatchPayload
  ): Promise<InboundEmailBindingDTO> => {
    return PATCH(
      fillRoute(APIRoutes.apiNeoWorkflowEmailBinding, {
        workflow_id: workflowId,
        binding_id: bindingId,
      }),
      payload,
      await authHeader()
    );
  };

  const deleteWorkflowEmailBindingAPI = async (
    workflowId: number,
    bindingId: number
  ): Promise<void> => {
    await DELETE(
      fillRoute(APIRoutes.apiNeoWorkflowEmailBinding, {
        workflow_id: workflowId,
        binding_id: bindingId,
      }),
      {},
      await authHeader()
    );
  };

  return {
    listWorkflowsAPI,
    listAvailableWorkflowsAPI,
    setWorkflowUserEnablementAPI,
    runClinicWorkflowAPI,
    createWorkflowAPI,
    getWorkflowGraphAPI,
    replaceWorkflowGraphAPI,
    restoreLastRunGraphAPI,
    keepCurrentGraphVersionAPI,
    generateNodeOutputSchemaAPI,
    checkNodeClinicScopeAPI,
    updateWorkflowAPI,
    deleteWorkflowAPI,
    runWorkflowAPI,
    getWorkflowRunProgressAPI,
    listWorkflowRunsAPI,
    getClinicWorkflowStatusAPI,
    listClinicWorkflowRunsAPI,
    getClinicWorkflowArtifactUrlAPI,
    listMondayBriefingDeliveriesAPI,
    getMondayBriefingDeliveryEmailAPI,
    getMondayBriefingRunAPI,
    listWorkflowRunConversationsAPI,
    listWorkflowEligibleClinicsAPI,
    listWorkflowEmailAddressesAPI,
    listWorkflowEmailBindingsAPI,
    createWorkflowEmailBindingAPI,
    patchWorkflowEmailBindingAPI,
    deleteWorkflowEmailBindingAPI,
  };
};
